import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap, Check, X, ChevronDown, Loader2, Gift, CreditCard, Trophy,
  ShieldCheck, Truck, Users, ClipboardCheck, Target, BookOpen, Building2, Facebook,
  Lock, LogIn, UserPlus, Unlock, Handshake, Sparkles, Gamepad2, Layers, BookMarked, Award,
} from "lucide-react";
import {
  COURSE_META, ROADMAP, MINDSET, COMPLIANCE, MODULES, ROLES, EQUIPMENT, GLOSSARY,
  KPIS, CAPSTONE, EXAM_GUIDE, LLC_STEPS, GAME_ITEMS, SCENARIOS, FB_TEMPLATES,
} from "../courseData";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import DispatchSim from "../components/DispatchSim";
import PayPalCheckout from "../components/PayPalCheckout";

const fmtPrice = (usd, cur) => {
  if (!cur || cur.code === "USD" || !cur.rate) return `$${usd}`;
  let amt = usd * cur.rate;
  amt = cur.zero_decimal ? Math.round(amt) : Math.round(amt * 100) / 100;
  const val = cur.zero_decimal ? amt.toLocaleString() : amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${cur.symbol}${val}`;
};

const ModuleCard = ({ m, completed, onComplete }) => {
  const [open, setOpen] = useState(m.n === 1);
  const [picked, setPicked] = useState(null);
  return (
    <div className={`tm-card ${open ? "open" : ""} ${completed ? "tm-complete" : ""}`} data-testid={`module-card-${m.n}`}>
      <button className="tm-head" onClick={() => setOpen((v) => !v)} data-testid={`module-toggle-${m.n}`}>
        <span className="tm-num">{completed ? <Check size={16} /> : String(m.n).padStart(2, "0")}</span>
        <span className="tm-title">{m.title}</span>
        <ChevronDown className={`tm-chev ${open ? "rot" : ""}`} size={20} />
      </button>
      {open && (
        <div className="tm-body">
          <div className="tm-learn">
            <span className="tm-learn-lbl">What you'll learn</span>
            <ul>{m.learn.map((l, i) => <li key={i}>{l}</li>)}</ul>
          </div>
          <ul className="tm-points">
            {m.points.map((p, i) => <li key={i}><Check size={15} /> {p}</li>)}
          </ul>
          <div className="tm-quiz">
            <div className="tm-quiz-q">Knowledge check: {m.quiz.q}</div>
            <div className="tm-quiz-opts">
              {m.quiz.options.map((o, i) => {
                const state = picked === null ? "" : i === m.quiz.a ? "right" : picked === i ? "wrong" : "";
                return (
                  <button key={i} className={`tm-opt ${state}`} onClick={() => setPicked(i)} data-testid={`quiz-${m.n}-opt-${i}`}>
                    {picked !== null && i === m.quiz.a && <Check size={14} />}
                    {picked === i && i !== m.quiz.a && <X size={14} />}
                    {o}
                  </button>
                );
              })}
            </div>
            {picked !== null && (
              <div className={`tm-result ${picked === m.quiz.a ? "ok" : "no"}`} data-testid={`quiz-result-${m.n}`}>
                {picked === m.quiz.a ? "Correct! Well done." : "Not quite — review the module above."}
              </div>
            )}
          </div>
          {onComplete && (
            <button className={`tm-complete-btn ${completed ? "on" : ""}`} onClick={() => onComplete(m.n, !completed)} data-testid={`module-complete-${m.n}`}>
              {completed ? <><Check size={15} /> Completed</> : "Mark module complete"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const MatchGame = () => {
  const [selected, setSelected] = useState(null);
  const [matched, setMatched] = useState([]);
  const [wrong, setWrong] = useState(null);
  const [matches] = useState(() => GAME_ITEMS.map((g) => g.match).sort(() => Math.random() - 0.5));
  const pickMatch = (match) => {
    if (!selected) return;
    const correct = GAME_ITEMS.find((g) => g.term === selected)?.match === match;
    if (correct) { setMatched((m) => [...m, selected]); setSelected(null); setWrong(null); }
    else { setWrong(match); }
  };
  const done = matched.length === GAME_ITEMS.length;
  return (
    <div className="game" data-testid="match-game">
      <h3><Trophy size={18} /> Warm-up: Match the Equipment to its Freight</h3>
      {done ? (
        <div className="game-win" data-testid="match-game-win">You matched them all — you're ready to source loads.</div>
      ) : (
        <div className="game-grid">
          <div className="game-col">
            {GAME_ITEMS.map((g) => (
              <button key={g.term} disabled={matched.includes(g.term)}
                className={`game-chip ${selected === g.term ? "sel" : ""} ${matched.includes(g.term) ? "done" : ""}`}
                onClick={() => { setSelected(g.term); setWrong(null); }} data-testid={`match-term-${g.term}`}>{g.term}</button>
            ))}
          </div>
          <div className="game-col">
            {matches.map((m) => {
              const isMatched = GAME_ITEMS.some((g) => g.match === m && matched.includes(g.term));
              return (
                <button key={m} disabled={isMatched}
                  className={`game-chip ${wrong === m ? "wrong" : ""} ${isMatched ? "done" : ""}`}
                  onClick={() => pickMatch(m)}>{m}</button>
              );
            })}
          </div>
        </div>
      )}
      <p className="game-progress">{matched.length} / {GAME_ITEMS.length} matched</p>
    </div>
  );
};

const ControlRoom = () => {
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const s = SCENARIOS[i];
  const finished = i >= SCENARIOS.length;
  const choose = (opt, idx) => { if (picked !== null) return; setPicked(idx); if (opt.correct) setScore((v) => v + 1); };
  const next = () => { setPicked(null); setI((v) => v + 1); };
  const restart = () => { setI(0); setPicked(null); setScore(0); };
  return (
    <div className="game control-room" data-testid="control-room-game">
      <h3><ShieldCheck size={18} /> Dispatch Control Room Challenge</h3>
      {finished ? (
        <div className="game-win" data-testid="control-room-result">
          You scored {score} / {SCENARIOS.length} on safe, compliant decisions.
          <button className="btn btn-primary cr-restart" onClick={restart} data-testid="control-room-restart">Play again</button>
        </div>
      ) : (
        <>
          <div className="cr-round">Round {i + 1} · {s.round}</div>
          <p className="cr-situation">{s.situation}</p>
          <div className="cr-opts">
            {s.options.map((o, idx) => {
              const state = picked === null ? "" : o.correct ? "right" : picked === idx ? "wrong" : "";
              return (<button key={idx} className={`game-chip ${state}`} onClick={() => choose(o, idx)} data-testid={`cr-opt-${idx}`}>{o.label}</button>);
            })}
          </div>
          {picked !== null && (
            <div className="cr-explain" data-testid="cr-explain">
              {s.explain}
              <button className="btn btn-primary cr-next" onClick={next} data-testid="cr-next">{i === SCENARIOS.length - 1 ? "See results" : "Next scenario"}</button>
            </div>
          )}
          <p className="game-progress">Score {score} / {SCENARIOS.length}</p>
        </>
      )}
    </div>
  );
};

const NegotiationGame = () => {
  const FLOOR = 850, TARGET = 950, BROKER_MAX = 1050;
  const [offer, setOffer] = useState(700);
  const [attempts, setAttempts] = useState(0);
  const [log, setLog] = useState([{ who: "broker", text: `I've got a load — I can pay $700 on this lane.` }]);
  const [result, setResult] = useState(null);

  const push = (who, text) => setLog((l) => [...l, { who, text }]);

  const counter = (amount) => {
    if (result) return;
    push("you", `Let's do $${amount}.`);
    if (amount <= BROKER_MAX) {
      push("broker", `Deal at $${amount}. Send the driver info.`);
      const profit = amount - FLOOR;
      setResult({ win: true, rate: amount, msg: amount >= TARGET ? `Excellent — you beat your target! Carrier profit over floor: $${profit}.` : `Booked above your floor. Carrier profit: $${profit}. A bit under target, but solid.` });
      return;
    }
    const nextAttempts = attempts + 1;
    setAttempts(nextAttempts);
    if (nextAttempts >= 3) {
      push("broker", `That's too high. My final is $${BROKER_MAX} — take it or leave it.`);
      setOffer(BROKER_MAX);
    } else {
      const mid = Math.round((offer + BROKER_MAX) / 2);
      push("broker", `Can't hit that. I can come up to $${mid}.`);
      setOffer(mid);
    }
  };
  const accept = () => {
    if (result) return;
    push("you", `I'll take $${offer}.`);
    const profit = offer - FLOOR;
    setResult({ win: offer >= FLOOR, rate: offer, msg: offer >= TARGET ? `Great close at $${offer}!` : offer >= FLOOR ? `Accepted at $${offer}. Profit over floor: $${profit}.` : `You booked below your cost floor — that load loses money.` });
  };
  const walk = () => { if (result) return; push("you", `That doesn't work for my carrier. I'll pass.`); setResult({ win: null, rate: 0, msg: `You walked away. Sometimes 'no' protects your carrier — but you booked $0 today.` }); };
  const restart = () => { setOffer(700); setAttempts(0); setResult(null); setLog([{ who: "broker", text: `I've got a load — I can pay $700 on this lane.` }]); };

  const counters = [900, 1000, 1100].filter((c) => c > offer);

  return (
    <div className="game nego" data-testid="negotiation-game">
      <h3><Handshake size={18} /> Rate Negotiation Trainer</h3>
      <p className="sim-intro">Your cost floor is <strong>${FLOOR}</strong>, target <strong>${TARGET}</strong>. Negotiate the broker up without losing the load.</p>
      <div className="nego-chat" data-testid="nego-chat">
        {log.map((m, i) => (<div key={i} className={`nego-msg ${m.who}`}><span className="nego-who">{m.who === "you" ? "You" : "Broker"}</span>{m.text}</div>))}
      </div>
      {result ? (
        <div className={`nego-result ${result.win ? "ok" : "no"}`} data-testid="nego-result">
          <p>{result.msg}</p>
          <button className="btn btn-primary" onClick={restart} data-testid="nego-restart">Try again</button>
        </div>
      ) : (
        <div className="nego-actions">
          <button className="game-chip" onClick={accept} data-testid="nego-accept">Accept ${offer}</button>
          {counters.map((c) => (<button key={c} className="game-chip" onClick={() => counter(c)} data-testid={`nego-counter-${c}`}>Counter ${c}</button>))}
          <button className="game-chip walkbtn" onClick={walk} data-testid="nego-walk">Walk away</button>
        </div>
      )}
    </div>
  );
};

const AccountBox = ({ onReady }) => {
  const { user, login, register, logout, loginWithGoogle } = useAuth();
  const [mode, setMode] = useState("signup");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (mode === "signup") await register({ name: f.name, email: f.email, password: f.password });
      else await login(f.email, f.password);
      onReady && onReady();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  if (user) {
    return (
      <div className="tr-account signedin" data-testid="student-signedin">
        <div><span className="tr-account-hi">Signed in as</span><strong>{user.email}</strong></div>
        <button className="tr-logout" onClick={logout} data-testid="student-logout">Log out</button>
      </div>
    );
  }

  return (
    <form className="tr-account" onSubmit={submit} data-testid="student-auth-form">
      <button type="button" className="btn btn-google" onClick={loginWithGoogle} data-testid="google-signin-btn">
        <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.1 29.1 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.1 29.1 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 34.9 26.7 36 24 36c-5.2 0-9.6-3.3-11.2-8l-6.5 5C9.6 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C41.8 35.9 44 30.5 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
        Continue with Google
      </button>
      <div className="tr-or-auth">or use email</div>
      <div className="tr-account-tabs">
        <button type="button" className={mode === "signup" ? "on" : ""} onClick={() => setMode("signup")} data-testid="auth-tab-signup"><UserPlus size={14} /> Create account</button>
        <button type="button" className={mode === "login" ? "on" : ""} onClick={() => setMode("login")} data-testid="auth-tab-login"><LogIn size={14} /> Log in</button>
      </div>
      {mode === "signup" && (
        <div className="field"><label>Full Name</label><input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="auth-name" /></div>
      )}
      <div className="form-row">
        <div className="field"><label>Email</label><input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="auth-email" /></div>
        <div className="field"><label>Password</label><input required type="password" minLength={6} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="auth-password" /></div>
      </div>
      {err && <p className="tr-auth-err" data-testid="auth-error">{err}</p>}
      <button className="btn btn-primary" disabled={busy} data-testid="auth-submit">{busy ? <Loader2 size={16} className="spin" /> : null} {mode === "signup" ? "Create student account" : "Log in"}</button>
    </form>
  );
};

const OFFERINGS = [
  { icon: Layers, t: "10 In-Depth Modules", d: "From business setup and compliance to sourcing, negotiation, dispatch operations, and scaling." },
  { icon: ClipboardCheck, t: "Interactive Knowledge Checks", d: "A quiz in every module to lock in what you learn as you go." },
  { icon: Gamepad2, t: "4 Hands-On Games", d: "Equipment match, Dispatch Control Room, a rate Negotiation Trainer, and a full Dispatch Day simulation." },
  { icon: BookMarked, t: "Trucking Glossary", d: "Master the vocabulary brokers and carriers expect you to know cold." },
  { icon: Users, t: "Roles & Equipment Guides", d: "Quick-reference cheat sheets for who does what and which trailer fits which freight." },
  { icon: Building2, t: "LLC Formation Walkthrough", d: "Step-by-step guidance to stand up your business the right way." },
  { icon: Award, t: "Final Exam & Capstone", d: "Prove your skills and build your own dispatch operating plan." },
  { icon: Gift, t: "Referral Bonuses", d: "Earn rewards for every friend who enrolls with your code." },
];

const Locked = () => (
  <section className="container tr-locked" data-testid="course-locked">
    <div className="tr-locked-hero">
      <span className="tr-locked-badge"><Sparkles size={14} /> Premium Masterclass</span>
      <h2>Everything you get inside</h2>
      <p>A complete, self-paced program built to take you from zero to a professional, compliant dispatch operation. Enroll to unlock full lifetime access.</p>
    </div>
    <div className="tr-offer-grid">
      {OFFERINGS.map((o, i) => {
        const Icon = o.icon;
        return (
          <div className="tr-offer-card" key={i}>
            <span className="tr-offer-ico"><Icon size={20} /></span>
            <h4>{o.t}</h4>
            <p>{o.d}</p>
          </div>
        );
      })}
    </div>
    <div className="tr-locked-cta">
      <Lock size={18} />
      <span>Full course access unlocks all modules, games, and resources.</span>
      <a href="#enroll" className="btn btn-primary" data-testid="locked-enroll-cta"><CreditCard size={16} /> Enroll to unlock</a>
    </div>
  </section>
);

const Training = () => {
  const { user } = useAuth();
  const [paying, setPaying] = useState(false);
  const [ref, setRef] = useState({ name: "", email: "" });
  const [refCode, setRefCode] = useState("");
  const [refBusy, setRefBusy] = useState(false);
  const [refInput, setRefInput] = useState("");
  const [currencies, setCurrencies] = useState([]);
  const [curCode, setCurCode] = useState("USD");
  const [content, setContent] = useState(null);
  const [progress, setProgress] = useState(null);
  const [buyer, setBuyer] = useState({ name: "", email: "" });

  const access = user?.course_access || "none";
  const full = access === "full" || user?.role === "admin";

  useEffect(() => {
    api.get("/currencies").then((r) => setCurrencies(r.data.currencies || [])).catch(() => {});
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) setBuyer({ name: user.name || "", email: user.email || "" });
  }, [user]);

  useEffect(() => {
    if (full && user) api.get("/course/progress").then((r) => setProgress(r.data)).catch(() => {});
  }, [full, user]);

  const cur = currencies.find((c) => c.code === curCode) || { code: "USD", symbol: "$", rate: 1, zero_decimal: false };
  const t = content?.training || {};
  const completedSet = new Set(progress?.completed || []);
  const toggleModule = async (n, done) => {
    try { const r = await api.post("/course/progress", { module: n, done }); setProgress(r.data); } catch (e) { /* noop */ }
  };

  const startPayment = async (packageId) => {
    if (!buyer.email || !buyer.name) { alert("Please enter your name and email to continue to payment."); document.getElementById("enroll")?.scrollIntoView({ behavior: "smooth" }); return; }
    setPaying(true);
    if (packageId !== "course_balance") {
      api.post("/enroll", { name: buyer.name, email: buyer.email, phone: "", plan: packageId === "course_full" ? "full" : "deposit", referral_code: refInput }).catch(() => {});
    }
    try {
      const r = await api.post("/payments/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
        email: buyer.email,
        name: buyer.name,
        currency: curCode.toLowerCase(),
      });
      window.location.href = r.data.checkout_url;
    } catch (e) { setPaying(false); alert("Could not start checkout. Please try again."); }
  };

  const getReferral = async (e) => {
    e.preventDefault();
    setRefBusy(true);
    try { const r = await api.post("/referrals", ref); setRefCode(r.data.code); }
    catch (err) { /* noop */ } finally { setRefBusy(false); }
  };

  const FullCourse = (
    <>
      <section className="container tr-dash" data-testid="course-dashboard">
        <div className="tr-dash-inner">
          <div>
            <span className="tr-dash-label">My Course Progress</span>
            <div className="tr-progress"><div className="tr-progress-bar" style={{ width: `${((progress?.completed?.length || 0) / 10) * 100}%` }} /></div>
            <span className="tr-dash-count" data-testid="progress-count">{progress?.completed?.length || 0} / 10 modules complete</span>
          </div>
          <Link to="/training/certificate" className={`btn ${progress?.certified ? "btn-primary" : "btn-pay"}`} data-testid="view-certificate-btn">
            <Award size={16} /> {progress?.certified ? "Get your certificate" : "Preview certificate"}
          </Link>
        </div>
      </section>
      <section className="container tr-info-grid" data-testid="mindset-section">
        <div className="tr-info-card"><h3><Target size={18} /> Professional Dispatch Mindset</h3><ul>{MINDSET.map((m, i) => <li key={i}><Check size={14} /> {m}</li>)}</ul></div>
        <div className="tr-info-card accent"><h3><ShieldCheck size={18} /> Critical Compliance Boundary</h3><ul>{COMPLIANCE.map((m, i) => <li key={i}><Check size={14} /> {m}</li>)}</ul></div>
      </section>
      <section className="container tr-roadmap" data-testid="roadmap-section"><h2><BookOpen size={20} /> Student Success Roadmap</h2><ol className="tr-steps">{ROADMAP.map((r, i) => <li key={i}>{r}</li>)}</ol></section>
      <section className="container tr-modules" data-testid="modules-section"><h2>The 10 Modules</h2>{MODULES.map((m) => <ModuleCard key={m.n} m={m} completed={completedSet.has(m.n)} onComplete={toggleModule} />)}</section>
      <section className="container tr-table-sec" data-testid="roles-section">
        <h2><Users size={20} /> Who Does What?</h2>
        <div className="tr-table"><div className="tr-thead"><span>Role</span><span>Main responsibility</span><span>Remember</span></div>
          {ROLES.map((r) => (<div className="tr-trow" key={r.role}><span className="tr-strong">{r.role}</span><span>{r.resp}</span><span>{r.remember}</span></div>))}
        </div>
      </section>
      <section className="container tr-table-sec" data-testid="equipment-section">
        <h2><Truck size={20} /> Equipment Cheat Sheet</h2>
        <div className="tr-table three"><div className="tr-thead"><span>Equipment</span><span>Best fit</span><span>Watch for</span></div>
          {EQUIPMENT.map((e) => (<div className="tr-trow" key={e.type}><span className="tr-strong">{e.type}</span><span>{e.fit}</span><span>{e.watch}</span></div>))}
        </div>
      </section>
      <section className="container tr-game-wrap"><MatchGame /></section>
      <section className="container tr-game-wrap"><ControlRoom /></section>
      <section className="container tr-game-wrap"><NegotiationGame /></section>
      <section className="container tr-game-wrap"><DispatchSim /></section>
      <section className="container tr-glossary" data-testid="glossary-section">
        <h2>Trucking Glossary</h2>
        <div className="gloss-grid">{GLOSSARY.map((g) => (<div className="gloss-item" key={g.t}><span className="gloss-t">{g.t}</span><span className="gloss-d">{g.d}</span></div>))}</div>
      </section>
      <section className="container tr-info-grid" data-testid="kpi-section">
        <div className="tr-info-card"><h3><Target size={18} /> KPIs You'll Track</h3><div className="tr-chips">{KPIS.map((k) => <span key={k} className="tr-chip">{k}</span>)}</div></div>
        <div className="tr-info-card"><h3><ClipboardCheck size={18} /> Final Capstone Sections</h3><div className="tr-chips">{CAPSTONE.map((k) => <span key={k} className="tr-chip">{k}</span>)}</div></div>
      </section>
      <section className="container tr-llc" data-testid="llc-section">
        <h2><Building2 size={20} /> How to Form Your LLC</h2>
        <p className="tr-sub">General education only — requirements vary by state. Always confirm with a qualified tax/legal professional.</p>
        <div className="tr-llc-grid">{LLC_STEPS.map((s, i) => (<div className="tr-llc-step" key={i}><span className="tr-llc-num">{i + 1}</span><div><span className="tr-strong">{s.t}</span><p>{s.d}</p></div></div>))}</div>
      </section>
      <section className="container tr-roadmap" data-testid="exam-section">
        <h2><GraduationCap size={20} /> Final Exam Study Guide</h2>
        <ul className="tr-steps plain">{EXAM_GUIDE.map((e, i) => <li key={i}>{e}</li>)}</ul>
        <p className="tr-sub">{COURSE_META.passing}</p>
      </section>
      <section className="container tr-fb" data-testid="facebook-section">
        <h2><Facebook size={20} /> Carrier-Sourcing Post Templates</h2>
        <div className="tr-fb-grid">{FB_TEMPLATES.map((f, i) => (<div className="tr-fb-card" key={i}><span className="tr-strong">{f.name}</span><p>{f.body}</p></div>))}</div>
      </section>
    </>
  );

  return (
    <div className="training" data-testid="training-page">
      <header className="tr-hero">
        <div className="container">
          <Link to="/" className="tr-back" data-testid="training-back-link">← Back to site</Link>
          <span className="eyebrow"><GraduationCap size={14} /> Flying Fleet Dispatch Academy</span>
          <h1>{t.hero_title || COURSE_META.title}</h1>
          <p>{t.hero_subtitle || `A complete 10-module masterclass to launch and grow your dispatching business. Includes ${COURSE_META.tagline.toLowerCase()}.`}</p>
          <div className="tr-price-row">
            <div className="tr-price"><span className="tr-price-num" data-testid="price-full">{fmtPrice(175, cur)}</span><span className="tr-price-lbl">full course</span></div>
            <div className="tr-price alt"><span className="tr-price-num" data-testid="price-deposit">{fmtPrice(50, cur)}</span><span className="tr-price-lbl">deposit to start</span></div>
          </div>
        </div>
      </header>

      {/* Enroll / account */}
      <section className="container tr-enroll" id="enroll" data-testid="enroll-section">
        {full ? (
          <div className="tr-unlocked" data-testid="course-unlocked-banner"><Unlock size={22} /><div><h2>You're enrolled — full access unlocked!</h2><p>Scroll down for all 10 modules, quizzes, the glossary, and three interactive games.</p></div></div>
        ) : (
          <>
            <h2>{access === "deposit" ? "Complete your enrollment" : "Enroll now"}</h2>
            {access === "deposit" && (
              <div className="tr-balance" data-testid="balance-due-banner">
                <Lock size={18} /> <span>Deposit received — <strong>balance due</strong>. Pay the remaining {fmtPrice(125, cur)} to unlock the full course.</span>
              </div>
            )}
            <div className="tr-currency">
              <label htmlFor="cur-select">Pay in your currency</label>
              <select id="cur-select" value={curCode} onChange={(e) => setCurCode(e.target.value)} data-testid="currency-select">
                {currencies.length === 0 && <option value="USD">USD — US Dollar ($)</option>}
                {currencies.map((c) => (<option key={c.code} value={c.code}>{`${c.code} — ${c.name} (${c.symbol})`}</option>))}
              </select>
              <span className="tr-currency-note">Prices update automatically. You'll be charged securely in {curCode}.</span>
            </div>

            <div className="tr-buyer">
              <div className="form-row">
                <div className="field"><label>Your Name</label><input value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} placeholder="Full name" data-testid="buyer-name" /></div>
                <div className="field"><label>Email (receipt & access)</label><input type="email" value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} placeholder="you@email.com" data-testid="buyer-email" /></div>
              </div>
            </div>

            {access !== "deposit" && (
              <>
                <div className="field tr-refcode"><label>Referral code (optional)</label><input value={refInput} onChange={(e) => setRefInput(e.target.value)} placeholder="FRND-1234" data-testid="enroll-referral" /></div>
                <div className="tr-pay-btns">
                  <button className="btn btn-pay" onClick={() => startPayment("course_deposit")} disabled={paying} data-testid="pay-deposit-btn">{paying ? <Loader2 size={16} className="spin" /> : <CreditCard size={16} />} Pay {fmtPrice(50, cur)} Deposit</button>
                  <button className="btn btn-primary" onClick={() => startPayment("course_full")} disabled={paying} data-testid="pay-full-btn">{paying ? <Loader2 size={16} className="spin" /> : <CreditCard size={16} />} Pay Full {fmtPrice(175, cur)}</button>
                </div>
                <div className="tr-or">— or pay the full course with PayPal —</div>
                <PayPalCheckout packageId="course_full" currency={curCode} email={buyer.email} name={buyer.name} onSuccess={() => window.location.reload()} />
              </>
            )}
            {access === "deposit" && (
              <>
                <div className="tr-pay-btns"><button className="btn btn-primary" onClick={() => startPayment("course_balance")} disabled={paying} data-testid="pay-balance-btn">{paying ? <Loader2 size={16} className="spin" /> : <Unlock size={16} />} Pay Balance {fmtPrice(125, cur)} &amp; Unlock</button></div>
                <div className="tr-or">— or pay the balance with PayPal —</div>
                <PayPalCheckout packageId="course_balance" currency={curCode} email={buyer.email} name={buyer.name} onSuccess={() => window.location.reload()} />
              </>
            )}
            <p className="tr-note">{t.deposit_note || "Deposit reserves your seat; the balance unlocks full course access. Only the full course unlocks all modules and games."}</p>

            <div className="tr-optional-acct">
              <span className="tr-optional-lbl">Optional — create an account to save your progress and earn a certificate</span>
              <AccountBox />
            </div>
          </>
        )}
      </section>

      {/* Gated content */}
      {full ? FullCourse : <Locked />}

      {/* Referral (public) */}
      <section className="tr-referral" data-testid="referral-section">
        <div className="container">
          <div className="ref-card">
            <div className="ref-copy">
              <span className="ref-icon"><Gift size={22} /></span>
              <div><h2>Refer a friend, earn a bonus</h2><p>Get your referral code and share it. Every friend who enrolls with your code earns you a referral bonus.</p></div>
            </div>
            {refCode ? (
              <div className="ref-code-box" data-testid="referral-code-box"><span className="ref-code-label">Your referral code</span><span className="ref-code" data-testid="referral-code">{refCode}</span><span className="ref-code-hint">Share this — you'll be credited for each enrollment!</span></div>
            ) : (
              <form className="ref-form" onSubmit={getReferral}>
                <input required placeholder="Your name" value={ref.name} onChange={(e) => setRef({ ...ref, name: e.target.value })} data-testid="referral-name" />
                <input required type="email" placeholder="Your email" value={ref.email} onChange={(e) => setRef({ ...ref, email: e.target.value })} data-testid="referral-email" />
                <button className="btn btn-primary" disabled={refBusy} data-testid="referral-submit">{refBusy ? <Loader2 size={16} className="spin" /> : <Gift size={16} />} Get My Code</button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Training;
